/**
 * Finance Redux Slice
 *
 * State management for finance domain including balance data.
 * Balance is CALCULATED from transactions on the backend, never stored.
 * Related: docs/engineering/CHARGE_SYSTEM.md
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Transaction, Payment, Statement, FinanceStats, FinanceFilter, PaymentFilter, BalanceSummary, BalanceCheckResult, PaymentsResponse, CreatePaymentPayload, Invoice, InvoiceFilter, InvoicesResponse, SapBalances } from '../domain/types'

export interface FinanceState {
  transactions: Transaction[]
  payments: Payment[]
  statement: Statement | null
  stats: FinanceStats
  filter: FinanceFilter
  loading: boolean
  error: string | null
  /** Customer balance summary - calculated on backend */
  balance: BalanceSummary | null
  /** Balance loading state */
  balanceLoading: boolean
  /** Balance error message */
  balanceError: string | null
  /** Last balance check result (for validation) */
  lastBalanceCheck: BalanceCheckResult | null
  /** Pagination metadata */
  pagination: {
    page: number
    pageSize: number
    pageCount: number
    total: number
  }
  /** Statement loading state */
  statementLoading: boolean
  /** Stats loading state */
  statsLoading: boolean
  /** Payment-specific loading */
  paymentsLoading: boolean
  /** Payment-specific pagination */
  paymentsPagination: {
    page: number
    pageSize: number
    pageCount: number
    total: number
  }
  /** Create payment loading */
  createPaymentLoading: boolean
  /** Create payment error */
  createPaymentError: string | null
  /** Invoice state */
  invoices: Invoice[]
  invoicesLoading: boolean
  invoicesPagination: {
    page: number
    pageSize: number
    pageCount: number
    total: number
  }
  selectedInvoice: Invoice | null
  selectedInvoiceLoading: boolean
  invoiceFilter: InvoiceFilter
  /** SAP balance data (finance + contract) */
  sapBalances: SapBalances | null
  sapBalancesLoading: boolean
  sapBalancesError: string | null
  sapBalancesRefreshing: boolean
  /** Retry payment SAP sync */
  retryPaymentSyncLoading: boolean
  retryPaymentSyncError: string | null
}

const initialState: FinanceState = {
  transactions: [],
  payments: [],
  statement: null,
  stats: { totalReceivable: { amount: 0, currency: 'SAR' }, totalPayable: { amount: 0, currency: 'SAR' }, pendingPayments: 0, overdueInvoices: 0 },
  filter: { type: 'all', status: 'all' },
  loading: false,
  error: null,
  balance: null,
  balanceLoading: false,
  balanceError: null,
  lastBalanceCheck: null,
  pagination: { page: 1, pageSize: 25, pageCount: 0, total: 0 },
  statementLoading: false,
  statsLoading: false,
  paymentsLoading: false,
  paymentsPagination: { page: 1, pageSize: 20, pageCount: 0, total: 0 },
  createPaymentLoading: false,
  createPaymentError: null,
  invoices: [],
  invoicesLoading: false,
  invoicesPagination: { page: 1, pageSize: 20, pageCount: 0, total: 0 },
  selectedInvoice: null,
  selectedInvoiceLoading: false,
  invoiceFilter: {},
  sapBalances: null,
  sapBalancesLoading: false,
  sapBalancesError: null,
  sapBalancesRefreshing: false,
  retryPaymentSyncLoading: false,
  retryPaymentSyncError: null,
}

export const financeSlice = createSlice({
  name: 'finance',
  initialState,
  reducers: {
    setTransactions: (state, action: PayloadAction<Transaction[]>) => { state.transactions = action.payload; state.loading = false; state.error = null },
    setPayments: (state, action: PayloadAction<Payment[]>) => { state.payments = action.payload; state.loading = false; state.error = null },
    setStatement: (state, action: PayloadAction<Statement>) => { state.statement = action.payload; state.loading = false; state.error = null },
    setStats: (state, action: PayloadAction<FinanceStats>) => { state.stats = action.payload },
    setFilter: (state, action: PayloadAction<FinanceFilter>) => { state.filter = { ...state.filter, ...action.payload } },
    setLoading: (state, action: PayloadAction<boolean>) => { state.loading = action.payload },
    setError: (state, action: PayloadAction<string | null>) => { state.error = action.payload; state.loading = false },
    searchTransactions: (state, action: PayloadAction<string>) => { state.filter = { ...state.filter, search: action.payload } },
    filterByType: (state, action: PayloadAction<FinanceFilter['type']>) => { state.filter = { ...state.filter, type: action.payload } },
    filterByStatus: (state, action: PayloadAction<FinanceFilter['status']>) => { state.filter = { ...state.filter, status: action.payload } },
    clearFinance: (state) => { state.transactions = []; state.payments = []; state.statement = null; state.error = null },

    // Balance actions - data is CALCULATED on backend, never stored
    fetchBalance: (state) => {
      state.balanceLoading = true
      state.balanceError = null
    },
    fetchBalanceSuccess: (state, action: PayloadAction<BalanceSummary>) => {
      state.balance = action.payload
      state.balanceLoading = false
      state.balanceError = null
    },
    fetchBalanceFailure: (state, action: PayloadAction<string>) => {
      state.balanceLoading = false
      state.balanceError = action.payload
    },

    // Balance check actions - validates if customer can place order
    checkBalance: (state, _action: PayloadAction<number>) => {
      state.balanceLoading = true
      state.balanceError = null
      state.lastBalanceCheck = null
    },
    checkBalanceSuccess: (state, action: PayloadAction<BalanceCheckResult>) => {
      state.lastBalanceCheck = action.payload
      state.balanceLoading = false
      state.balanceError = null
    },
    checkBalanceFailure: (state, action: PayloadAction<string>) => {
      state.balanceLoading = false
      state.balanceError = action.payload
      state.lastBalanceCheck = null
    },

    // Clear balance check result
    clearBalanceCheck: (state) => {
      state.lastBalanceCheck = null
      state.balanceError = null
    },

    // Fetch transactions actions
    fetchTransactions: (state, _action: PayloadAction<{ filter?: FinanceFilter; page?: number; pageSize?: number }>) => {
      state.loading = true
      state.error = null
    },
    fetchTransactionsSuccess: (state, action: PayloadAction<{
      transactions: Transaction[]
      meta: { page: number; pageSize: number; pageCount: number; total: number }
    }>) => {
      state.transactions = action.payload.transactions
      state.pagination = action.payload.meta
      state.loading = false
      state.error = null
    },
    fetchTransactionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Fetch stats actions
    fetchStats: (state) => {
      state.statsLoading = true
    },
    fetchStatsSuccess: (state, action: PayloadAction<FinanceStats>) => {
      state.stats = action.payload
      state.statsLoading = false
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      state.statsLoading = false
      state.error = action.payload
    },

    // Sync payments from SAP (on-demand)
    syncPayments: (state) => {
      state.paymentsLoading = true
    },
    syncPaymentsSuccess: (state) => {
      // After sync completes, fetchPayments will be dispatched to reload
    },
    syncPaymentsFailure: (state, _action: PayloadAction<string>) => {
      // Sync failed silently — still show existing data
    },

    // Create payment actions
    createPayment: (state, _action: PayloadAction<CreatePaymentPayload>) => {
      state.createPaymentLoading = true
      state.createPaymentError = null
    },
    createPaymentSuccess: (state) => {
      state.createPaymentLoading = false
      state.createPaymentError = null
    },
    createPaymentFailure: (state, action: PayloadAction<string>) => {
      state.createPaymentLoading = false
      state.createPaymentError = action.payload
    },

    // Fetch payments actions
    fetchPayments: (state, _action: PayloadAction<{ filter?: PaymentFilter; page?: number; pageSize?: number; sort?: string }>) => {
      state.paymentsLoading = true
      state.error = null
    },
    fetchPaymentsSuccess: (state, action: PayloadAction<PaymentsResponse>) => {
      state.payments = action.payload.payments
      state.paymentsPagination = action.payload.meta
      state.paymentsLoading = false
      state.error = null
    },
    fetchPaymentsFailure: (state, action: PayloadAction<string>) => {
      state.paymentsLoading = false
      state.error = action.payload
    },

    // Invoice actions
    syncInvoices: (state) => {
      state.invoicesLoading = true
    },
    syncInvoicesSuccess: (state) => {
      // After sync, fetchInvoices will reload
    },
    syncInvoicesFailure: (state, _action: PayloadAction<string>) => {
      // Sync failed silently
    },

    fetchInvoices: (state, _action: PayloadAction<{ filter?: InvoiceFilter; page?: number; pageSize?: number; sort?: string }>) => {
      state.invoicesLoading = true
      state.error = null
    },
    fetchInvoicesSuccess: (state, action: PayloadAction<InvoicesResponse>) => {
      state.invoices = action.payload.invoices
      state.invoicesPagination = action.payload.meta
      state.invoicesLoading = false
      state.error = null
    },
    fetchInvoicesFailure: (state, action: PayloadAction<string>) => {
      state.invoicesLoading = false
      state.error = action.payload
    },

    fetchInvoiceDetail: (state, _action: PayloadAction<string>) => {
      state.selectedInvoiceLoading = true
    },
    fetchInvoiceDetailSuccess: (state, action: PayloadAction<Invoice>) => {
      state.selectedInvoice = action.payload
      state.selectedInvoiceLoading = false
    },
    fetchInvoiceDetailFailure: (state, action: PayloadAction<string>) => {
      state.selectedInvoiceLoading = false
      state.error = action.payload
    },

    setInvoiceFilter: (state, action: PayloadAction<InvoiceFilter>) => {
      state.invoiceFilter = { ...state.invoiceFilter, ...action.payload }
    },

    clearSelectedInvoice: (state) => {
      state.selectedInvoice = null
    },

    // SAP balance actions (on-demand from CPI)
    fetchSapBalances: (state) => {
      state.sapBalancesLoading = true
      state.sapBalancesError = null
    },
    fetchSapBalancesCached: (state) => {
      if (!state.sapBalances) {
        state.sapBalancesLoading = true
      }
      state.sapBalancesError = null
    },
    fetchSapBalancesSuccess: (state, action: PayloadAction<SapBalances>) => {
      state.sapBalances = action.payload
      state.sapBalancesLoading = false
      state.sapBalancesError = null
    },
    fetchSapBalancesFailure: (state, action: PayloadAction<string>) => {
      state.sapBalancesLoading = false
      state.sapBalancesError = action.payload
    },

    // Background refresh (stale-while-revalidate)
    refreshSapBalances: (state) => {
      state.sapBalancesRefreshing = true
    },
    refreshSapBalancesSuccess: (state, action: PayloadAction<SapBalances>) => {
      state.sapBalances = action.payload
      state.sapBalancesRefreshing = false
    },
    refreshSapBalancesFailure: (state, _action: PayloadAction<string>) => {
      state.sapBalancesRefreshing = false
    },

    // Retry payment SAP sync
    retryPaymentSync: (state, _action: PayloadAction<string>) => {
      state.retryPaymentSyncLoading = true
      state.retryPaymentSyncError = null
    },
    retryPaymentSyncSuccess: (state) => {
      state.retryPaymentSyncLoading = false
      state.retryPaymentSyncError = null
    },
    retryPaymentSyncFailure: (state, action: PayloadAction<string>) => {
      state.retryPaymentSyncLoading = false
      state.retryPaymentSyncError = action.payload
    },

    // Fetch statement actions
    fetchStatement: (state, _action: PayloadAction<{ from: string; to: string }>) => {
      state.statementLoading = true
      state.error = null
    },
    fetchStatementSuccess: (state, action: PayloadAction<Statement>) => {
      state.statement = action.payload
      state.statementLoading = false
      state.error = null
    },
    fetchStatementFailure: (state, action: PayloadAction<string>) => {
      state.statementLoading = false
      state.error = action.payload
    },
  },
})

export const financeActions = financeSlice.actions
export const financeReducer = financeSlice.reducer
